import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  Stack,
  Divider,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { manualAdjustWallet, fetchWalletLogs, searchUsers } from '../../services/wallet';

const DEFAULT_ADJUST_FORM = {
  user_id: '',
  balance_type: 'balance',
  action: 'credit',
  amount: '',
  reason: '',
};

const WalletManagement = () => {
  const { t } = useTranslation(['walletManagement', 'common']);
  const [userOptions, setUserOptions] = useState([]);
  const [adminOptions, setAdminOptions] = useState([]);
  const [filterUserOptions, setFilterUserOptions] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  const [adjustForm, setAdjustForm] = useState(DEFAULT_ADJUST_FORM);
  const [savingAdjustment, setSavingAdjustment] = useState(false);

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logFilters, setLogFilters] = useState({ user_id: '', operator_id: '', action: '', balance_type: '' });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const debouncedSearch = useRef(null);

  const handleUserSearch = useCallback((query) => {
    setUserSearch(query);
    if (debouncedSearch.current) {
      clearTimeout(debouncedSearch.current);
    }
    if (!query.trim()) {
      setUserOptions([]);
      return;
    }
    debouncedSearch.current = setTimeout(async () => {
      setLoadingUsers(true);
      try {
        const response = await searchUsers({ search: query, role: 'user' });
        setUserOptions(response?.results || []);
      } catch (error) {
        setSnackbar({ open: true, message: error.message || t('messages.failedToSearchUsers'), severity: 'error' });
      } finally {
        setLoadingUsers(false);
      }
    }, 400);
  }, []);

  const loadFilterUserOptions = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await searchUsers({ role: 'user' });
      setFilterUserOptions(response?.results || []);
    } catch (error) {
      setSnackbar({ open: true, message: error.message || t('messages.failedToLoadUserList'), severity: 'error' });
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadAdminOptions = useCallback(async () => {
    setLoadingAdmins(true);
    try {
      const response = await searchUsers({ role: 'admin' });
      setAdminOptions(response?.results || []);
    } catch (error) {
      setSnackbar({ open: true, message: error.message || t('messages.failedToLoadAdminList'), severity: 'error' });
    } finally {
      setLoadingAdmins(false);
    }
  }, []);

  const loadLogs = useCallback(async (filters = logFilters) => {
    setLogsLoading(true);
    try {
      const response = await fetchWalletLogs({
        user_id: filters.user_id || undefined,
        operator_id: filters.operator_id || undefined,
        action: filters.action || undefined,
        balance_type: filters.balance_type || undefined,
        page_size: 50,
      });
      setLogs(response?.results || response || []);
    } catch (error) {
      setSnackbar({ open: true, message: error.message || t('messages.failedToLoadLogs'), severity: 'error' });
    } finally {
      setLogsLoading(false);
    }
  }, [logFilters]);

  useEffect(() => {
    loadFilterUserOptions();
    loadAdminOptions();
    loadLogs();
  }, [loadFilterUserOptions, loadAdminOptions, loadLogs]);

  const handleAdjustChange = (field) => (event) => {
    setAdjustForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleLogFilterChange = (field) => (event) => {
    setLogFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleApplyLogFilters = () => {
    loadLogs({ ...logFilters });
  };

  const handleResetLogFilters = () => {
    const reset = { user_id: '', operator_id: '', action: '', balance_type: '' };
    setLogFilters(reset);
    loadLogs(reset);
  };

  const handleSubmitAdjustment = async () => {
    if (!adjustForm.user_id) {
      setSnackbar({ open: true, message: t('messages.selectUser'), severity: 'warning' });
      return;
    }

    const amount = parseFloat(adjustForm.amount);
    if (Number.isNaN(amount) || amount <= 0) {
      setSnackbar({ open: true, message: t('messages.amountGreaterThanZero'), severity: 'warning' });
      return;
    }

    if (!adjustForm.reason.trim()) {
      setSnackbar({ open: true, message: t('messages.provideReason'), severity: 'warning' });
      return;
    }

    setSavingAdjustment(true);
    try {
      const response = await manualAdjustWallet({
        user_id: adjustForm.user_id,
        balance_type: adjustForm.balance_type,
        action: adjustForm.action,
        amount,
        reason: adjustForm.reason.trim(),
      });
      setSnackbar({ open: true, message: response?.message || t('messages.adjustedSuccessfully'), severity: 'success' });
      setAdjustForm(DEFAULT_ADJUST_FORM);
      loadLogs();
    } catch (error) {
      const isForbidden = error?.response?.status === 403;
      const message = isForbidden
        ? t('messages.failedToAdjustPermission')
        : error.response?.data?.detail || error.message || t('messages.failedToAdjust');
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setSavingAdjustment(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        overflowX: 'hidden',
      }}
    >
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        {t('title')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('sections.manualAdjustment')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <TextField
                label={t('form.searchUsers')}
                value={userSearch}
                onChange={(event) => handleUserSearch(event.target.value)}
                fullWidth
                margin="dense"
              />

              <FormControl fullWidth margin="dense" disabled={loadingUsers}>
                <InputLabel>{t('form.user')}</InputLabel>
                <Select
                  label={t('form.user')}
                  value={adjustForm.user_id}
                  onChange={handleAdjustChange('user_id')}
                  MenuProps={{ style: { maxHeight: 300 } }}
                >
                  {loadingUsers ? (
                    <MenuItem value="">
                      <em>{t('form.loading')}</em>
                    </MenuItem>
                  ) : userOptions.length ? (
                    userOptions.map((user) => (
                      <MenuItem key={user.sid} value={user.sid}>
                        {user.fullname || user.username || user.email} ({user.email})
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">
                      <em>{t('form.noUsersFound')}</em>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 0.75 }}>
                {t('form.balanceType')}
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={adjustForm.balance_type}
                onChange={(_, value) => value && setAdjustForm((prev) => ({ ...prev, balance_type: value }))}
                aria-label={t('form.balanceType')}
              >
                <ToggleButton value="balance">{t('balanceTypes.balance')}</ToggleButton>
                <ToggleButton value="bonus_balance">{t('balanceTypes.bonusBalance')}</ToggleButton>
              </ToggleButtonGroup>

              <FormControl fullWidth margin="dense">
                <InputLabel>{t('form.action')}</InputLabel>
                <Select
                  label={t('form.action')}
                  value={adjustForm.action}
                  onChange={handleAdjustChange('action')}
                >
                  <MenuItem value="credit">{t('actions.credit')}</MenuItem>
                  <MenuItem value="debit">{t('actions.debit')}</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label={t('form.amount')}
                type="number"
                value={adjustForm.amount}
                onChange={handleAdjustChange('amount')}
                fullWidth
                margin="dense"
                inputProps={{ min: 0, step: '0.01' }}
              />

              <TextField
                label={t('form.reason')}
                value={adjustForm.reason}
                onChange={handleAdjustChange('reason')}
                fullWidth
                margin="dense"
                multiline
                rows={3}
              />

              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 2 }}
                onClick={handleSubmitAdjustment}
                disabled={savingAdjustment}
                startIcon={savingAdjustment ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {savingAdjustment ? t('buttons.submitting') : t('buttons.submit')}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t('sections.manualAdjustmentLogs')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('sections.logsDescription')}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={handleApplyLogFilters}>{t('buttons.applyFilters')}</Button>
                  <Button variant="outlined" onClick={handleResetLogFilters}>{t('buttons.reset')}</Button>
                </Stack>
              </Stack>

              <Grid container spacing={2} sx={{ my: 2 }}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth size="small" disabled={loadingUsers}>
                    <InputLabel>{t('filters.userFilter')}</InputLabel>
                    <Select
                      label={t('filters.userFilter')}
                      value={logFilters.user_id}
                      onChange={handleLogFilterChange('user_id')}
                      MenuProps={{ style: { maxHeight: 300 } }}
                    >
                      <MenuItem value="">
                        <em>{t('filters.allUsers')}</em>
                      </MenuItem>
                      {filterUserOptions.map((user) => (
                        <MenuItem key={user.sid} value={user.sid}>
                          {user.fullname || user.username || user.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth size="small" disabled={loadingAdmins}>
                    <InputLabel>{t('filters.operatorFilter')}</InputLabel>
                    <Select
                      label={t('filters.operatorFilter')}
                      value={logFilters.operator_id}
                      onChange={handleLogFilterChange('operator_id')}
                      MenuProps={{ style: { maxHeight: 300 } }}
                    >
                      <MenuItem value="">
                        <em>{t('filters.allOperators')}</em>
                      </MenuItem>
                      {adminOptions.map((admin) => (
                        <MenuItem key={admin.id} value={admin.id}>
                          {admin.fullname || admin.username || admin.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('filters.actionFilter')}</InputLabel>
                    <Select
                      label={t('filters.actionFilter')}
                      value={logFilters.action}
                      onChange={handleLogFilterChange('action')}
                    >
                      <MenuItem value="">
                        <em>{t('filters.allActions')}</em>
                      </MenuItem>
                      <MenuItem value="credit">{t('actions.credit')}</MenuItem>
                      <MenuItem value="debit">{t('actions.debit')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('filters.balanceTypeFilter')}</InputLabel>
                    <Select
                      label={t('filters.balanceTypeFilter')}
                      value={logFilters.balance_type}
                      onChange={handleLogFilterChange('balance_type')}
                    >
                      <MenuItem value="">
                        <em>{t('filters.allBalanceTypes')}</em>
                      </MenuItem>
                      <MenuItem value="balance">{t('balanceTypes.balance')}</MenuItem>
                      <MenuItem value="bonus_balance">{t('balanceTypes.bonusBalance')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {logsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('table.date')}</TableCell>
                        <TableCell>{t('table.user')}</TableCell>
                        <TableCell>{t('table.balanceType')}</TableCell>
                        <TableCell>{t('table.action')}</TableCell>
                        <TableCell>{t('table.amount')}</TableCell>
                        <TableCell>{t('table.operator')}</TableCell>
                        <TableCell>{t('table.reason')}</TableCell>
                        <TableCell>{t('table.ip')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {logs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography variant="body2" color="text.secondary">
                              {t('table.noOperations')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        logs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
                            <TableCell>{log.user_display || log.user_email || `User ID ${log.user}`}</TableCell>
                            <TableCell>
                              {log.balance_type === 'bonus_balance'
                                ? t('balanceTypes.bonusBalance')
                                : t('balanceTypes.balance')}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={log.action === 'credit' ? t('actions.credit') : t('actions.debit')}
                                color={log.action === 'credit' ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>₦{Number(log.amount).toLocaleString()}</TableCell>
                            <TableCell>{log.operator_display || log.operator_email || `Admin ID ${log.operator}`}</TableCell>
                            <TableCell>{log.reason}</TableCell>
                            <TableCell>{log.operator_ip || 'N/A'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WalletManagement;

